import {
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Spin,
	message,
} from "antd";
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { ApiResponse, CarMediaError } from "types";

import type { UploadFile, UploadProps } from "antd/es/upload";
import MultipleDragImage, {
	PhotoItemProps,
} from "components/upload-multiple-image-dnd";
import UploadVideo from "components/upload-video/UploadVideo";
import {
	CAR_BODY_TYPE,
	CAR_COLOR,
	CAR_DOORS,
	CAR_FUEL,
	CAR_ORIGIN,
	CAR_SEATS,
	CAR_STATE,
	CAR_TAX_TYPE,
	CAR_TRANSMISSION,
	COMMON_STATUS,
	DEFAULT_CURRENCY,
	ENGINE_SIZE_UNIT,
	MAX_CAR_PHOTOS,
	MAX_SIZE_VIDEO,
	MAX_YEAR_SEARCH,
	MIN_YEAR_SEARCH,
} from "constants/constants";
import { Brand } from "features/brand/types";
import { Car, CarPhoto, MasterDataOption } from "features/cars/types";
import { Model } from "features/model/types";
import {
	getCarBrandsAPI,
	getCarModelsAPI,
	getMerchantStoresAPI,
	getMerchantsAPI,
} from "service/apis";
import { putFileS3 } from "service/uploadService";

import { t } from "@lingui/macro";
import { FormInstance } from "antd/es/form";
import { RcFile } from "antd/lib/upload";
import SelectInfinity from "components/select-infinity/SelectInfinity";
import { validateMedia } from "features/cars/create";
import {
	selectMerchantStores,
	setMerchantStores,
} from "features/cars/redux/creating-cars.slice";
import { Merchant } from "features/merchant/types";
import { Store } from "features/store/types";
import heic2any from "heic2any";
import { useDispatch, useSelector } from "react-redux";
import { BaseQuery, MerchantQuery } from "service/queries";
import { getDiscount, getLastItemInArray, translate } from "utils";
import { Link } from "react-router-dom";
import { APP_PATH } from "constants/path";

/* eslint-disable no-template-curly-in-string */
const validateMessages = {
	required: "${label} is required!",
	types: {
		email: "${label} is not a valid email!",
		number: "${label} is not a valid number!",
	},
	number: {
		range: "${label} must be between ${min} and ${max}",
		min: "${label} must be minimum ${min}",
	},
};

const formItemLayout = {
	labelCol: {
		xs: { span: 24 },
		sm: { span: 8 },
	},
	wrapperCol: {
		xs: { span: 24 },
		sm: { span: 16 },
	},
};

const getListYearFilter = () => {
	const years = [];
	for (let index = MAX_YEAR_SEARCH; index >= MIN_YEAR_SEARCH; index--) {
		years.push(index);
	}
	return years;
};

const getImages = (car?: Car) => {
	if (!car?.photos) return [];
	return (car?.photos as CarPhoto[]).filter(
		photo => photo?.classifiedType !== "video",
	);
};

const getVideo = (car?: Car) => {
	if (!car?.photos) return [];
	const videos = (car?.photos as CarPhoto[]).filter(
		photo => photo?.classifiedType === "video",
	);

	return videos[0];
};

export interface Props {
	car: Car;
	onChangeForm: (id: string | number, value: any) => void;
	parentRef: React.RefObject<CarFormComponentProps>;
	isFirstSubmit: boolean;
}

export interface CarFormComponentProps {
	isLoading: boolean;
	form: FormInstance<any>;
	id: string;
	setMediaError: React.Dispatch<React.SetStateAction<CarMediaError>>;
}

const { Option } = Select;
const { TextArea } = Input;

const getMasterOptions = (data: MasterDataOption[]): MasterDataOption[] =>
	data.map(option => ({ label: translate(option.label), value: option.value }));

const CarForm: React.FC<Props> = ({
	car,
	onChangeForm,
	parentRef,
	isFirstSubmit,
}) => {
	const dispatch = useDispatch();
	const merchantStores = useSelector(selectMerchantStores);
	const [form] = Form.useForm();
	const brand = Form.useWatch("brand", form);
	const model = Form.useWatch("model", form);
	const manufactureYear = Form.useWatch("manufactureYear", form);

	const [brands, setBrands] = useState<Brand[]>([]);
	const [models, setModels] = useState<Model[]>([]);
	const [yearManu, setYearManu] = useState<number[]>([]);

	const [photos, setPhotos] = useState<any[]>(getImages(car));
	const photoClassifedType = useRef("internal");
	const photosLength = useRef<number>(photos.length);
	const [video, setVideo] = useState<any>(getVideo(car));

	const [loadingVideo, setLoadingVideo] = useState(false);
	const [isUploadingImage, setIsUploadingImage] = useState(false);

	const [files, setFiles] = useState<UploadFile<any>[]>([]);
	const filesLength = useRef<number>(0);
	const [progressVideo, setProgressVideo] = useState(0);
	const [queryMerchant, setQueryMerchant] = useState<MerchantQuery>({
		skip: 0,
		take: 10,
		total: 0,
	});

	const [queryStore, setQueryStore] = useState<BaseQuery>({
		skip: 0,
		take: 20,
	});

	const [mediaError, setMediaError] = useState<CarMediaError>({
		internal: "",
		external: "",
		video: "",
	});

	const [merchantList, setMerchantList] = useState<Merchant[]>([]);

	useImperativeHandle(parentRef, () => ({
		isLoading: loadingVideo,
		form: form,
		id: car.id.toString(),
		setMediaError: setMediaError,
	}));

	useEffect(() => {
		form.setFieldsValue(car);
		setPhotos(getImages(car));
		setVideo(getVideo(car));
		const title = getTitle();
		form.setFieldValue("name", title);
	}, [car]);

	useEffect(() => {
		if (files.length > 0) {
			const promises: Promise<any>[] = [];
			files.forEach(file => {
				const uploadPromiseS3 = handleConvertAndUploadS3(
					(file?.originFileObj ?? file) as File,
				);
				promises.push(uploadPromiseS3);
			});
			Promise.all(promises).then(results => {
				const newUrls = results.map(value => {
					photosLength.current += 1;
					return {
						...value,
						classifiedType: photoClassifedType.current,
						position: photosLength.current,
					};
				});
				const isUploadVideo = newUrls.findIndex(value =>
					value.url.includes("videos"),
				);
				if (isUploadVideo !== -1) {
					setLoadingVideo(false);
					newUrls[isUploadVideo].classifiedType = "video";
					setVideo(newUrls[isUploadVideo]);
				} else {
					const lastIndexAddType = getLastItemInArray(
						photos,
						photo => photo.classifiedType === photoClassifedType.current,
						true,
					);
					const listTempPhoto = [...photos];
					listTempPhoto.splice(lastIndexAddType + 1, 0, ...newUrls);
					setPhotos(listTempPhoto);
				}
				filesLength.current = 0;
				setIsUploadingImage(false);
			});
		}
	}, [files]);

	useEffect(() => {
		getCarBrandsAPI().then((result: ApiResponse<Brand[]>) => {
			setBrands(result.data);
		});
	}, []);

	useEffect(() => {
		const title = getTitle();
		form.setFieldValue("name", title);
	}, [manufactureYear, brand, model, models, brands]);

	useEffect(() => {
		if (typeof car.brand === "number") {
			handleUpdateModels(car.brand);
		}
	}, []);

	useEffect(() => {
		if (validateChange()) {
			updateStoredPhotos();
			if (!isFirstSubmit) {
				validateMedia([...photos, video], setMediaError);
			}
		}
	}, [video, photos]);

	const getTitle = () => {
		let title = "";
		const modelFound = models.find(el => el.id === model);
		const brandFound = brands.find(el => el.id === brand);
		if (brandFound?.name === "Other") {
			form.setFieldValue("model", null);
			title = "Liberty Carz";
		} else if (modelFound?.name === "Other") {
			title = `${brandFound?.name ?? ""} ${manufactureYear ?? ""}`;
		} else {
			title = `${brandFound?.name ?? ""} ${modelFound?.name ?? ""} ${
				manufactureYear ?? ""
			}`;
		}
		return title;
	};

	const onUploadProgress = useCallback(
		(progressData: { loaded: number; total: number }) => {
			if (progressData.loaded === progressData.total) {
				setProgressVideo(0);
			} else {
				setProgressVideo(
					Math.round((progressData.loaded / progressData.total) * 100),
				);
			}
		},
		[loadingVideo],
	);

	const validateChange = () => {
		const currentFiles = car.photos as CarPhoto[];
		const currentVideos = currentFiles?.filter(
			file => file.classifiedType === "video",
		);

		if (
			(currentVideos.length === 0 && video?.url) ||
			(currentVideos.length > 0 && !video?.url)
		)
			return true;

		const currentPhotos = currentFiles.filter(
			file => file.classifiedType !== "video",
		);
		if (currentPhotos.length !== photos.length) return true;

		for (let index = 0; index < photos.length; index++) {
			if (
				photos[index]?.url !== currentPhotos[index]?.url ||
				photos[index]?.classifiedType !== currentPhotos[index]?.classifiedType
			)
				return true;
		}
		return false;
	};

	const updateStoredPhotos = () => {
		if (video?.url) {
			onChangeForm(car.id, { photos: [...photos, video] });
		} else {
			onChangeForm(car.id, { photos });
		}
	};

	const getOptions = (list: string[] | number[]): ReactNode[] =>
		list.map((option, index) => (
			<Option value={option} key={index}>
				{option}
			</Option>
		));

	const getLabels = (list: MasterDataOption[]): string[] =>
		list.map(option => option.label);

	const handleConvertAndUploadS3 = async (fileBlob: File) => {
		try {
			const fileType = fileBlob?.name?.split(".");
			if (
				fileType[fileType.length - 1]?.toLowerCase() === "heic" ||
				fileType[fileType.length - 1]?.toLowerCase() === "heif"
			) {
				const jpegBlob = await heic2any({
					blob: fileBlob,
					toType: "image/jpeg",
				});
				const newFileJPEG = new File([jpegBlob as Blob], fileBlob.name, {
					type: "image/jpeg",
				});
				return putFileS3(newFileJPEG);
			} else {
				return putFileS3(
					fileBlob,
					fileBlob.type.includes("video") ? onUploadProgress : undefined,
				);
			}
		} catch (error) {
			console.error("Error converting HEIC to JPEG:", error);
			throw error;
		}
	};

	const handleAddPhoto: UploadProps["onChange"] = async ({
		file,
		fileList,
		...props
	}) => {
		if (fileList.length + photos.length > MAX_CAR_PHOTOS) {
			if (fileList.map(file => file.uid).indexOf(file.uid) === 0) {
				message.error(`The image of the car is maximum ${MAX_CAR_PHOTOS}`);
			}
			return;
		}
		if (fileList.length !== filesLength.current) {
			setIsUploadingImage(true);
			filesLength.current = fileList.length;
			const { photoType } = props as any;
			photoClassifedType.current = photoType;
			setFiles(fileList);
		}
	};

	const handleAddVideo = (file: RcFile) => {
		setFiles([file]);
	};

	const handleDeleteVideo = (): void => {
		setVideo("");
	};

	const handleDeletePhoto = (deletePhoto: PhotoItemProps) => {
		const newPhotos = photos.filter(photo => photo !== deletePhoto);
		setPhotos(newPhotos);
	};

	const handleChangeBrand = (value: number) => {
		const brand = brands.find(b => b.id === value);
		if (brand) {
			handleUpdateModels(brand.id!);
			setYearManu([]);
		}
		form.setFieldValue("manufactureYear", null);
	};

	const handleChangeModel = (value: number) => {
		form.setFieldValue("manufactureYear", null);

		const model = models.find(b => b.id === value);
		if (model && model.id && model.manufactureYears) {
			setYearManu(model?.manufactureYears);
		}
	};

	const handleUpdateModels = (brandId: number) => {
		const modelQuery = { brand: brandId, skip: 0 };
		getCarModelsAPI(modelQuery).then((result: ApiResponse<Model[]>) => {
			const indexOtherOption = result?.data.findIndex(
				value => value?.name.toLowerCase() === "other",
			);
			if (indexOtherOption === -1) {
				setModels(result.data);
				return;
			} else {
				const tempArray = [...result.data];
				tempArray.push(tempArray.splice(indexOtherOption, 1)[0]);
				setModels(tempArray);
			}
		});
	};

	const handleSearchMerchant = (searchValue: string) => {
		setQueryMerchant((prev: MerchantQuery) => {
			if (searchValue) {
				return {
					...prev,
					skip: 0,
					fullName: searchValue,
				};
			} else {
				const prevState = prev;
				delete prevState.fullName;
				return {
					...prevState,
					skip: 0,
				};
			}
		});
	};

	const getActiveMerchant = (query: MerchantQuery) => {
		const activeQuery = { ...query, status: "enable", isRemoved: false };
		return getMerchantsAPI(activeQuery);
	};

	const handleNewStoreList = (stores: Store[]) => {
		dispatch(setMerchantStores(stores));
	};

	const getMerchantStores = (merchantId: string) => {
		const take = 10;
		getMerchantStoresAPI(merchantId, take, {
			status: COMMON_STATUS.enabled.value,
		})
			.then(stores => {
				handleNewStoreList(stores.data);
				if (stores.total > take) {
					getMerchantStoresAPI(merchantId, stores.total, {
						status: COMMON_STATUS.enabled.value,
					}).then(stores => handleNewStoreList(stores.data));
				}
			})
			.catch(error => console.log("get merchant stores", error));
	};

	const numberGreaterThan1 = (value: number, message: string) => {
		if (value === null) {
			return Promise.reject();
		}
		if (Number(value) < 1) {
			return Promise.reject(translate(message));
		}
		return Promise.resolve();
	};

	const getTranslatedOptions = (list: MasterDataOption[]): MasterDataOption[] =>
		list.map(option => ({
			label: translate(option.label),
			value: option.value,
		}));

	const renderFormByType = () => {
		if (car?.customer?.id) {
			return (
				<Col span={12}>
					<span>{t`Car owner`}</span>
					<span className="text-red-500">*</span>
					<Form.Item
						name={["customer", "fullName"]}
						label={t`Car owner`}
						rules={[{ required: true }]}
					>
						<Link
							to={`${APP_PATH.customers}/${car?.customer?.id}`}
							target="_blank"
							className="text-blue-500 underline"
						>
							{car?.customer?.fullName ?? ""}
						</Link>
					</Form.Item>
				</Col>
			);
		}
		return (
			<>
				<Col span={12}>
					<span>{t`Merchant`}</span>
					<span className="text-red-500">*</span>
					<Form.Item
						name={["merchant", "fullName"]}
						label={t`Merchant`}
						rules={[{ required: true }]}
					>
						<SelectInfinity
							query={queryMerchant}
							setQuery={setQueryMerchant}
							renderList={merchantList}
							setRenderList={setMerchantList}
							fetchMoreFunc={getActiveMerchant}
							onSearch={handleSearchMerchant}
							value={car?.merchant?.fullName || ""}
							placeholder={t`Merchant`}
							className={"w-full"}
							showSearch
							optionRender={(item: Merchant, index: number) => (
								<Option value={`${item.fullName} ${item.username}`} key={index}>
									<p>{item.fullName}</p>
									<span className="text-sm text-[#95a5a6]">
										{item.username}
									</span>
								</Option>
							)}
						/>
					</Form.Item>
				</Col>

				<Col span={12}>
					<p>
						{t`Store`}
						<span className="text-red-500">*</span>
					</p>
					<Form.Item
						name={["store", "name"]}
						label={t`Store`}
						rules={[{ required: true }]}
					>
						<SelectInfinity
							query={queryStore}
							setQuery={setQueryStore}
							renderList={merchantStores ?? []}
							setRenderList={setMerchantStores}
							value={car?.store?.name ?? undefined}
							placeholder={t`Select store`}
							className="w-full"
							showSearch
							isNotLoadingOnSearch
							optionRender={(item: Store, index: number) => (
								<Option value={item.name} key={index}>
									<p>{item.name}</p>
									<span className="text-sm text-[#95a5a6]">{item.address}</span>
								</Option>
							)}
						/>
					</Form.Item>
				</Col>
			</>
		);
	};

	return (
		<div>
			<Divider orientation="left">
				{t`Photos`} & {t`Video`}
			</Divider>
			<Spin spinning={isUploadingImage}>
				<MultipleDragImage
					photos={photos}
					setPhotos={setPhotos}
					onDelete={handleDeletePhoto}
					onAdd={handleAddPhoto}
					maximumPhotos={MAX_CAR_PHOTOS}
					error={mediaError}
				/>
			</Spin>

			<UploadVideo
				video={video?.url}
				onAdd={handleAddVideo}
				onDelete={handleDeleteVideo}
				maxSizeVideo={MAX_SIZE_VIDEO}
				loading={loadingVideo}
				setLoading={setLoadingVideo}
				progressVideo={progressVideo}
				errorMessage={mediaError.video}
				maximumDesc="Maximum 1 video"
			/>

			<Divider orientation="left">{t`Detail Information`}</Divider>
			<Form
				{...formItemLayout}
				form={form}
				initialValues={car}
				layout="vertical"
				scrollToFirstError
				validateMessages={validateMessages}
				className="pl-40"
				onValuesChange={object => {
					const key = Object.keys(object)[0];
					const value: any = Object.values(object)[0];
					const brandFound = brands.find(el => el.id === brand);
					if (key === "brand") {
						object.model = null;
						object.manufactureYear = null;
					}

					if (key === "model") {
						object.manufactureYear = null;
					}
					if (key === "merchant") {
						object.merchant.id = merchantList.find(
							merchant =>
								`${merchant.fullName} ${merchant.username}` ===
								object.merchant.fullName,
						)?.id;
						getMerchantStores(object.merchant.id);
					}
					if (key === "store") {
						const store = merchantStores?.find(s => s.name === value.name);
						object.carStore = store?.id;
					}
					const modelFound = models.find(el => el.id === model);
					if (brandFound?.name === "Other") {
						object.name = "Liberty Carz";
					} else if (modelFound?.name === "Other") {
						object.name = `${brandFound?.name ?? ""} ${manufactureYear ?? ""}`;
					} else {
						object.name = `${brandFound?.name ?? ""} ${
							modelFound?.name ?? ""
						} ${manufactureYear ?? ""}`;
					}

					onChangeForm(car.id, object);
				}}
			>
				<Row gutter={8}>
					<Col span={12}>
						<span>{t`Title`}</span>
						<Form.Item name="name" label={t`Title`}>
							<Input readOnly />
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Brand`}</span>
						<span className="text-red-500">*</span>
						<Form.Item
							name={"brand"}
							rules={[{ required: true }]}
							label={t`Brand`}
						>
							<Select
								placeholder={t`Select brand`}
								showSearch
								onChange={handleChangeBrand}
								filterOption={(inputValue, option) => {
									return (
										!option ||
										typeof option.children !== "string" ||
										((option.children || "") as string)
											.toLocaleLowerCase()
											.indexOf(inputValue.toLocaleLowerCase()) > -1
									);
								}}
							>
								{brands.map((brand, index) => (
									<Option value={brand.id} key={index}>
										{brand.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={8}>
					<Col span={12}>
						<span>{t`Model`}</span>
						<span className="text-red-500">*</span>
						<Form.Item
							name="model"
							rules={[{ required: true }]}
							label={t`Model`}
						>
							<Select
								placeholder={t`Select model`}
								showSearch
								onChange={handleChangeModel}
								filterOption={(inputValue, option) => {
									return (
										!option ||
										typeof option.children !== "string" ||
										((option.children || "") as string)
											.toLocaleLowerCase()
											.indexOf(inputValue.toLocaleLowerCase()) > -1
									);
								}}
							>
								{models.map((option, index) => (
									<Option key={index} value={option.id}>
										{option.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Year of manufacture`}</span>
						<span className="text-red-500">*</span>
						<Form.Item
							name="manufactureYear"
							label={t`Year of manufacture`}
							rules={[
								{ required: true },
								{
									type: "number",
									min: MIN_YEAR_SEARCH,
									max: MAX_YEAR_SEARCH,
								},
							]}
						>
							<Select placeholder={t`Select year`}>
								{yearManu.length > 0
									? yearManu.map((item, idx) => (
											<Option value={item} key={idx}>
												{item}
											</Option>
									  ))
									: getOptions(getListYearFilter())}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				{model === "Other" && (
					<Row gutter={8}>
						<Col span={12}>
							<span>{t`Note`}</span>
							<span className="text-red-500">*</span>
							<Form.Item
								rules={[{ required: true }]}
								name="note"
								label={t`Note`}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
				)}

				<Row gutter={8}>
					<Col span={12}>
						<div className="ant-col ant-form-item-control ant-col-xs-24 ant-col-sm-16">
							<div className="flex justify-between">
								<div className="w-fit">
									<span>{t`Selling Price`}</span>
									<span className="text-red-500">*</span>
								</div>
								<Form.Item
									name="isNegotiation"
									valuePropName="checked"
									className="mb-0 min-h-20 w-fit"
								>
									<Checkbox>{t`Negotiation`}</Checkbox>
								</Form.Item>
							</div>
						</div>
						<Form.Item
							name="price"
							label={t`Selling Price`}
							rules={[
								{ required: true },
								() => ({
									validator(_, value) {
										return numberGreaterThan1(
											value,
											"Selling Price must be minimum 1",
										);
									},
								}),
							]}
						>
							<InputNumber
								addonAfter={DEFAULT_CURRENCY}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Minimum Selling Price`}</span>
						<Form.Item
							name="minPrice"
							label={t`Minimum Selling Price`}
							rules={[{ type: "number", min: 0 }]}
						>
							<InputNumber
								addonAfter={DEFAULT_CURRENCY}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Discount`}</span>
						<Form.Item name="minPrice" rules={[{ type: "number", min: 0 }]}>
							<div className="flex gap-5">
								<InputNumber
									disabled={true}
									value={
										getDiscount(
											car?.sellingPrice,
											car?.discountValue,
											car?.discountType,
										).price
									}
									addonAfter={DEFAULT_CURRENCY}
									style={{ width: "60%" }}
								/>
								<InputNumber
									disabled={true}
									value={
										getDiscount(
											car?.sellingPrice,
											car?.discountValue,
											car?.discountType,
										).percent
									}
									addonAfter={"%"}
									style={{ width: "40%" }}
								/>
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Car Status`}</span>
						<Form.Item name="state" label={t`Car Status`}>
							<Select
								placeholder={t`Select status`}
								options={getMasterOptions(CAR_STATE)}
							/>
						</Form.Item>
					</Col>
					{form.getFieldValue("state") === CAR_STATE[1].value && (
						<Col span={12}>
							<span>{t`Tax Type`}</span>

							<Form.Item name="taxType" label={t`Tax Type`}>
								<Select
									placeholder={t`Select tax type`}
									options={getTranslatedOptions(CAR_TAX_TYPE)}
								/>
							</Form.Item>
						</Col>
					)}
					<Col span={12}>
						<span>{t`Transmission`}</span>
						<Form.Item name="transmission" label={t`Transmission`}>
							<Select
								placeholder={t`Select transmission`}
								options={getMasterOptions(CAR_TRANSMISSION)}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Fuel`}</span>
						<Form.Item name="fuel" label={t`Fuel`}>
							<Select
								placeholder={t`Select fuel`}
								options={getMasterOptions(CAR_FUEL)}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Color`}</span>
						<Form.Item name="color" label={t`Color`}>
							<Select
								placeholder={t`Select color`}
								options={getMasterOptions(CAR_COLOR)}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Seats`}</span>
						<Form.Item name="seat" label={t`Seats`}>
							<Select placeholder={t`Select seats`}>
								{getOptions(CAR_SEATS)}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Engine Size`}</span>
						<Form.Item
							name="engineSize"
							label={t`Engine Size`}
							rules={[{ type: "number", min: 0 }]}
						>
							<InputNumber
								style={{ width: "100%" }}
								addonAfter={ENGINE_SIZE_UNIT}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Body Type`}</span>
						<Form.Item name="bodyType" label={t`Body Type`}>
							<Select placeholder={t`Select body type`}>
								{getOptions(getLabels(CAR_BODY_TYPE))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Origin`}</span>
						<Form.Item name="origin" label={t`Origin`}>
							<Select
								placeholder={t`Select origin`}
								options={getMasterOptions(CAR_ORIGIN)}
							/>
						</Form.Item>
					</Col>

					{renderFormByType()}

					<Col span={12}>
						<span>{t`Mileage`}</span>
						<Form.Item
							name="distanceDriven"
							label={t`Mileage`}
							rules={[{ type: "number", min: 0 }]}
						>
							<InputNumber style={{ width: "100%" }} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<span>{t`Doors`}</span>
						<Form.Item name="doors" label={t`Doors`}>
							<Select placeholder={t`Select doors`}>
								{getOptions(CAR_DOORS)}
							</Select>
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={0}>
					<span>{t`Description`}</span>
					<Col span={24}>
						<Form.Item
							label={t`Description`}
							name="description"
							labelCol={{ span: 4 }}
							wrapperCol={{ span: 24 }}
						>
							<TextArea rows={4} className="w-[83.5%]" />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</div>
	);
};

export default CarForm;
